import * as React from "react";
import { inject, observer } from "mobx-react";
import { CarSubLOBs } from "src/stores/wizard/state/typings";
import { WizardCarPWAProps, WizardCarPWAState } from "./typings";
import { WizardCarPWAView } from "./WizardCarPWAView";
import { Experiment, isVariantEnabled } from "../../utility/experiment";

@inject("wizardState", "routeStore", "compositionStore")
@observer
export class WizardCarPWA extends React.Component<WizardCarPWAProps, WizardCarPWAState> {
  constructor(props: WizardCarPWAProps) {
    super(props);
    this.state = {
      isDiffDropOff: false,
      isDriverAgeInRange: true,
      isAARPChecked: false,
    };

    const { wizardState, flexComponent, context } = this.props;
    const { carWizardState, globalWizardState } = wizardState;

    carWizardState.overrideConfig(() => {
      carWizardState.config.elementId = flexComponent.id;
      carWizardState.config.heading.fixedTitle = flexComponent.model.fixedLobTitle;
      carWizardState.config.lastSearch.useLastSearchForSingleLOB = flexComponent.model.useLastSearchForSingleLOB;
      carWizardState.config.heading.lobSubtitleBreakPoint = flexComponent.model.lobSubtitleBreakPoint;
      carWizardState.config.heading.hideLobTitle = flexComponent.model.hideLobTitle;
      carWizardState.config.includeGT = flexComponent.model.includeGT ?? carWizardState.config.includeGT;
      carWizardState.config.disableVendorCouponCode =
        flexComponent.model.disableVendorCouponCode ?? carWizardState.config.disableVendorCouponCode;
      carWizardState.config.iHaveDiscount = carWizardState.config.disableVendorCouponCode
        ? "wizard.car.having.brand.title"
        : "wizard.car.having.discount";

      const typeahedGoogleSuggestionActive = isVariantEnabled(
        context,
        Experiment.Typeahead_Enabling_Google_Autocomplete_Suggestion_for_Car_Lob
      );
      const addGoogleFallback = (essFeatures: string): string => {
        if (typeahedGoogleSuggestionActive && essFeatures.indexOf("google") < 0) {
          return `${essFeatures}|google`;
        }

        return essFeatures;
      };
      const essFeaturesDest = carWizardState.config.location.destination.essAdapterConfig.features;
      const essFeaturesOrigin = carWizardState.config.location.origin.essAdapterConfig.features;
      carWizardState.config.location.destination.essAdapterConfig.features = addGoogleFallback(essFeaturesDest);
      carWizardState.config.location.origin.essAdapterConfig.features = addGoogleFallback(essFeaturesOrigin);
    });

    if (globalWizardState.shouldUpdateCookie) {
      carWizardState.updateStateFromCookie();
    }
    carWizardState.updateDateFromConfig();
  }

  public componentDidMount() {
    const { wizardState, flexComponent } = this.props;
    const { carWizardState } = wizardState;

    if (this.isGTFlexModule(flexComponent.name)) {
      carWizardState.updateSubLOBState(CarSubLOBs.GROUND_TRANSPORTATION);
    } else {
      this.setState(() => ({
        isDiffDropOff: wizardState.carWizardState.isDifferentDropOff,
      }));
      carWizardState.updateSubLOBState(CarSubLOBs.RENTAL);
    }
  }

  /* Making sure the subLob does not reset to car for wizard-gt-pwa module (gtOnly module) when the component updates */
  public componentDidUpdate() {
    const { wizardState, flexComponent } = this.props;

    if (this.isGTFlexModule(flexComponent.name)) {
      wizardState.carWizardState.updateSubLOBState(CarSubLOBs.GROUND_TRANSPORTATION);
    }
  }

  private isGTFlexModule = (flexModuleName: string) => flexModuleName === "wizard-gt-pwa";

  private setSelectedTabOptions = (selectedTab: CarSubLOBs) => {
    const { wizardState } = this.props;

    wizardState.carWizardState.updateSubLOBState(selectedTab);
  };

  private toggleDifferentDropOff = () => {
    const { wizardState } = this.props;

    this.setState(({ isDiffDropOff }) => ({
      isDiffDropOff: !isDiffDropOff,
    }));
    wizardState.carWizardState.toggleIsDifferentDropOff();
  };

  private setIsDifferentDropOff = (differentDropOff: boolean) => {
    const { wizardState } = this.props;

    this.setState(() => ({
      isDiffDropOff: differentDropOff,
    }));

    wizardState.carWizardState.setIsDifferentDropoff(differentDropOff);
  };

  private toggleDriverAgeInRange = () => {
    this.setState(({ isDriverAgeInRange }) => ({
      isDriverAgeInRange: !isDriverAgeInRange,
    }));
  };

  private onAARPChange = () => {
    this.setState(({ isAARPChecked }) => ({
      isAARPChecked: !isAARPChecked,
    }));
  };

  private toggleBookRoundtrip = () => {
    const { wizardState } = this.props;

    wizardState.carWizardState.toggleBookRoundtrip();
  };

  public onTabOptionSelected = (selectedTab: CarSubLOBs) => {
    this.setSelectedTabOptions(selectedTab);
  };

  public onDifferentDropOffSelected = () => this.toggleDifferentDropOff();

  public onDriverAgeInRange = () => this.toggleDriverAgeInRange();

  public render() {
    const { isDiffDropOff, isDriverAgeInRange, isAARPChecked } = this.state;
    const { wizardState, flexComponent, compositionStore } = this.props;
    const { showPageHeading } = flexComponent.model;

    return (
      <WizardCarPWAView
        {...this.props}
        onTabOptionSelected={this.onTabOptionSelected}
        toggleBookRoundtrip={this.toggleBookRoundtrip}
        onDifferentDropOffSelected={this.onDifferentDropOffSelected}
        setIsDifferentDropOff={this.setIsDifferentDropOff}
        onDriverAgeInRange={this.onDriverAgeInRange}
        onAARPChange={this.onAARPChange}
        isDiffDropOff={isDiffDropOff}
        isDriverAgeInRange={isDriverAgeInRange}
        isAARPChecked={isAARPChecked}
        showGTOnly={this.isGTFlexModule(flexComponent.name)}
        pageHeading={(showPageHeading && compositionStore?.composition?.pageHeading) || undefined}
        selectedTab={wizardState.carWizardState.subLOBState}
      />
    );
  }
}

export default WizardCarPWA;
